// src/pages/CheckoutPage.js
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import InputMask from 'react-input-mask';

import { CartContext } from '../CartContext';
import { useConfig } from '../contexts/ConfigContext';

import { 
  ArrowLeft, Phone, MessageSquare, 
  Banknote, Check, Gift, ChevronRight, Plus, 
  Home, Briefcase, MapPin, AlertTriangle
} from 'lucide-react';
import { triggerHaptic, triggerNotification } from '../utils/haptics';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useContext(CartContext);
  
  const config = useConfig();
  
  const tg = window.Telegram?.WebApp;
  const tgUser = tg?.initDataUnsafe?.user;
  const userId = tgUser?.id;

  const [deliveryType, setDeliveryType] = useState('delivery');

  const [savedAddresses, setSavedAddresses] = useState([]); 
  const [phone, setPhone] = useState(() => {
    try {
      return localStorage.getItem('vitrina_user_phone') || '';
    } catch {
      return '';
    }
  });
  const [addressData, setAddressData] = useState(null); 
  const [comment, setComment] = useState(''); 
  const [userBalance, setUserBalance] = useState(0);
  const [useBonuses, setUseBonuses] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- СОСТОЯНИЯ ПРОМОКОДА ---
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');

  const phoneInputRef = useRef(null);

  const maxBonusPercent = config.maxBonusPercent;

  const showAlert = (message, title = 'Внимание') => {
    if (tg?.showPopup) {
      tg.showPopup({ title, message, buttons: [{ type: 'ok', text: 'Понятно' }] });
    } else {
      alert(message);
    }
  };

  const parseAddress = (addrRaw) => {
      try {
          const parsed = JSON.parse(addrRaw);
          if (parsed && parsed.coords && Array.isArray(parsed.coords)) {
              parsed.coords = parsed.coords.map(c => parseFloat(c));
          }
          if (parsed && parsed.main_text) return parsed;
          return { 
              main_text: parsed.value || addrRaw, 
              label: parsed.label || 'other', 
              details: parsed.details || {}, 
              coords: parsed.coords 
          };
      } catch {
          return { main_text: addrRaw, label: 'other', details: {}, coords: null };
      }
  };

  useEffect(() => {
    if (cartItems.length === 0) {
        navigate('/');
        return;
    }

    const fetchData = async () => {
      try {
        if (!userId) {
            console.error("Critical: Telegram User ID not found");
            return;
        }
        
        const profileRes = await axios.get(`/api/profile?user_id=${userId}`);
        setUserBalance(profileRes.data.balance || 0);
        
        if (profileRes.data.addresses) {
            const formatted = profileRes.data.addresses.map(a => ({ 
                id: a.id, 
                ...parseAddress(a.address) 
            }));
            setSavedAddresses(formatted);
        }
      } catch (e) { 
          console.error("Fetch error:", e);
      }
    };
    fetchData();
  }, [cartItems, navigate, userId]);

  const handleInputFocus = () => {
    setTimeout(() => {
      if (phoneInputRef.current) {
        phoneInputRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 500);
  };

  const cartTotal = getCartTotal();
  const minOrder = config.minOrderAmount || 0; 
  const isMinAmountMet = cartTotal >= minOrder;
  const remainingForMin = minOrder - cartTotal;
  const availableBonuses = Math.floor(userBalance);
  
  const deliveryPrice = deliveryType === 'pickup' ? 0 : (config.deliveryCost ?? 0);

  // --- ФУНКЦИИ ПРОМОКОДА ---
  const handleApplyPromo = async () => {
    if (!promoCodeInput.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const res = await axios.post('/api/check-promo', {
        user_id: userId,
        code: promoCodeInput.trim(),
        cart_total: cartTotal
      });
      if (res.data.status === 'ok') {
        setAppliedPromo(res.data);
        // CHANGED: Если применили промокод, жестко отключаем бонусы!
        setUseBonuses(false);
        triggerHaptic('success');
      }
    } catch (err) {
      setPromoError(err.response?.data?.error || 'Ошибка промокода');
      setAppliedPromo(null);
      triggerHaptic('error');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCodeInput('');
    setPromoError('');
    triggerHaptic('light');
  };

  // --- РАСЧЕТЫ ЦЕН (СТРОГОЕ ИСКЛЮЧЕНИЕ) ---
  let promoDiscountAmt = 0;
  let bonusAmt = 0;

  if (appliedPromo) {
      // 1. Считаем скидку промокода
      if (appliedPromo.discount_type === 'percent') {
          promoDiscountAmt = cartTotal * (appliedPromo.discount_value / 100);
      } else {
          promoDiscountAmt = appliedPromo.discount_value;
      }
      promoDiscountAmt = Math.min(promoDiscountAmt, cartTotal); // Не больше суммы корзины
      // Бонусы принудительно 0, если есть промокод
      bonusAmt = 0; 
  } else if (useBonuses) {
      // 2. Считаем бонусы ТОЛЬКО ЕСЛИ НЕТ промокода
      const maxPossibleBonus = Math.floor((cartTotal + deliveryPrice) * (maxBonusPercent / 100));
      bonusAmt = Math.min(maxPossibleBonus, availableBonuses);
  }

  const discountedCartTotal = cartTotal - promoDiscountAmt;
  const finalToPay = Math.max(1, discountedCartTotal + deliveryPrice - bonusAmt);


  const handleSubmit = async () => {
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (cleanPhone.length < 11) {
      triggerNotification('error');
      showAlert('Пожалуйста, введите корректный номер телефона.');
      return;
    }
    if (deliveryType === 'delivery' && !addressData) { 
      triggerNotification('error');
      showAlert('Укажите адрес доставки.');
      return; 
    }
    if (!isMinAmountMet) { 
      triggerNotification('error');
      showAlert(`Минимальная сумма заказа: ${minOrder} ₽`);
      return; 
    }
    if (!userId) {
      triggerNotification('error');
      showAlert('Ошибка авторизации. Перезапустите бота.');
      return;
    }

    setIsSubmitting(true);
    triggerHaptic('medium');

    try {
      localStorage.setItem('vitrina_user_phone', phone);
    } catch {}

    let finalAddressString = '';
    let fullComment = comment;

    if (deliveryType === 'pickup') {
      const pickupAddr = config.pickup?.address || 'Самовывоз из заведения';
      finalAddressString = `Самовывоз: ${pickupAddr}`;
    } else {
      finalAddressString = addressData.main_text || addressData.value;
      const d = addressData.details || {};
      const parts = [];
      if (d.flat) parts.push(`кв. ${d.flat}`);
      if (d.floor) parts.push(`эт. ${d.floor}`);
      if (d.entrance) parts.push(`под. ${d.entrance}`);
      if (d.intercom) parts.push(`код ${d.intercom}`);
      if (parts.length > 0) finalAddressString += ` (${parts.join(', ')})`;
      if (d.comment) fullComment = d.comment + (fullComment ? `. ${fullComment}` : ""); 
    }

    const orderPayload = {
      user_id: userId,
      items: cartItems,
      total_amount: (cartTotal + deliveryPrice), // Оригинальная сумма
      delivery_type: deliveryType,
      delivery_cost: deliveryPrice,
      payment_method: 'cash', 
      contact: { 
          name: tgUser?.first_name || 'Гость', 
          phone: phone, 
          email: '' 
      },
      address: { value: finalAddressString, comment: "" },
      promo_code: appliedPromo ? appliedPromo.code : null, // Отправляем промокод
      used_bonuses: bonusAmt, // Отправляем фактические бонусы
      comment: fullComment 
    };

    try {
      const res = await axios.post('/api/create-order', orderPayload);
      if (res.data.status === 'ok') {
          triggerNotification('success'); 
          clearCart(); 
          if (tg?.showPopup) {
              tg.showPopup({
                  title: 'Заказ принят!',
                  message: `Заказ #${res.data.order_id} успешно оформлен. Владелец скоро свяжется с вами!`,
                  buttons: [{type: 'ok', text: 'Отлично'}]
              }, () => {
                  tg.close();
              });
          } else {
              navigate('/profile/orders');
          }
      } else { 
          throw new Error(res.data.error || 'Ошибка при сохранении заказа');
      }
    } catch (error) { 
        console.error("Submit error:", error); 
        triggerNotification('error');
        showAlert(`Не удалось оформить заказ: ${error.response?.data?.error || error.message}`); 
        setIsSubmitting(false); 
    }
  };

  const handleSelectSavedAddress = (addr) => { 
      triggerHaptic('light'); 
      setAddressData(addr); 
  };
  
  const getIcon = (label) => {
      if (label === 'work') return <Briefcase size={18} className="text-purple-500" />;
      if (label === 'home') return <Home size={18} className="text-blue-500" />;
      return <MapPin size={18} className="text-tg-hint" />;
  };

  if (config.isLoading) {
      return (
        <div className="min-h-screen bg-tg-bg flex items-center justify-center p-10 text-center text-tg-hint animate-pulse">
            Загрузка параметров доставки...
        </div>
      );
  }

  const isPhoneValid = phone.replace(/\D/g, '').length === 11;
  const isAddressValid = deliveryType === 'pickup' || !!addressData;
  const isFormValid = isPhoneValid && isAddressValid && isMinAmountMet;
  const buttonText = isSubmitting ? 'Оформляем...' : `Заказать за ${finalToPay} ₽`;

  return (
    <div className="min-h-[110vh] bg-tg-bg pb-36 text-tg-text transition-colors duration-200">
      
      {/* Header */}
      <div className="px-3 pb-3 pt-tg flex items-center sticky top-0 bg-tg-bg/95 backdrop-blur-md z-20 border-b border-gray-100/10 transition-all">
        <button onClick={() => navigate(-1)} className="mr-3 text-tg-text p-1 active:opacity-50">
            <ArrowLeft size={22} />
        </button>
        <h1 className="text-lg font-bold">Оформление заказа</h1>
      </div>

      <div className="p-3 space-y-4">
        
        {/* ПЕРЕКЛЮЧАТЕЛЬ: ДОСТАВКА / САМОВЫВОЗ */}
        <div className="flex bg-tg-secondary-bg p-1 rounded-2xl border border-gray-100/5">
            <button 
                type="button"
                onClick={() => { setDeliveryType('delivery'); triggerHaptic('selection'); }}
                className={`flex-1 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                    deliveryType === 'delivery' 
                        ? 'bg-tg-button text-white shadow-sm' 
                        : 'text-tg-hint active:opacity-70'
                }`}
            >
                🚚 Доставка
            </button>
            <button 
                type="button"
                onClick={() => { setDeliveryType('pickup'); triggerHaptic('selection'); }}
                className={`flex-1 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                    deliveryType === 'pickup' 
                        ? 'bg-tg-button text-white shadow-sm' 
                        : 'text-tg-hint active:opacity-70'
                }`}
            >
                🛍️ Самовывоз
            </button>
        </div>

        {/* АДРЕС ИЛИ ТОЧКА САМОВЫВОЗА */}
        {deliveryType === 'pickup' ? (
            <div className="bg-tg-secondary-bg p-4 rounded-xl flex items-center gap-3 border border-gray-100/5">
                <div className="bg-tg-button/10 text-tg-button p-2 rounded-full shrink-0">
                    <MapPin size={20} />
                </div>
                <div>
                    <p className="text-[10px] uppercase font-bold text-tg-hint tracking-wider">Пункт выдачи заказа</p>
                    <p className="text-sm font-bold text-tg-text">{config.pickup?.address || 'Уточняйте адрес у администратора'}</p>
                </div>
            </div>
        ) : (
            <div className="space-y-2">
                {savedAddresses.length > 0 && (
                    <div className="bg-tg-secondary-bg rounded-xl overflow-hidden mb-2 border border-gray-100/5">
                        <p className="text-[10px] text-tg-hint font-bold uppercase px-4 pt-3 tracking-wider">Ваши адреса</p>
                        {savedAddresses.map(addr => (
                            <div 
                                key={addr.id} 
                                onClick={() => handleSelectSavedAddress(addr)} 
                                className={`flex items-center justify-between p-4 border-b border-tg-bg last:border-0 active:bg-black/5 transition-colors ${addressData?.id === addr.id ? 'bg-tg-button/5' : ''}`}
                            >
                                <div className="flex items-center gap-3">
                                    {getIcon(addr.label)}
                                    <div>
                                        <p className="text-sm font-bold leading-none mb-1">
                                            {addr.label === 'home' ? 'Дом' : addr.label === 'work' ? 'Работа' : 'Адрес'}
                                        </p>
                                        <p className="text-xs text-tg-hint line-clamp-1">{addr.main_text}</p>
                                    </div>
                                </div>
                                {addressData?.id === addr.id && <Check size={16} className="text-tg-button" />}
                            </div>
                        ))}
                    </div>
                )}
                
                <button 
                    onClick={() => navigate('/profile/addresses/add')} 
                    className="w-full bg-tg-secondary-bg p-4 rounded-xl flex items-center gap-3 active:scale-98 transition-transform border border-dashed border-tg-hint/20"
                >
                    <div className="bg-tg-button/10 text-tg-button p-2 rounded-full shrink-0"><Plus size={20} /></div>
                    <div className="text-left flex-1">
                        <p className="text-sm font-bold">{addressData && !addressData.id ? "Выбран новый адрес" : "Новый адрес"}</p>
                        <p className="text-xs text-tg-hint">{addressData && !addressData.id ? addressData.main_text : "Добавить место доставки"}</p>
                    </div>
                    <ChevronRight className="text-tg-hint shrink-0" size={18} />
                </button>
            </div>
        )}

        {/* ПРЕДУПРЕЖДЕНИЕ О МИН СУММЕ */}
        {!isMinAmountMet && (
          <div className="bg-red-500/5 border border-red-500/20 p-3 rounded-xl flex items-center gap-3">
            <AlertTriangle className="text-red-500" size={20} />
            <div className="text-xs text-red-500 font-medium">
                Минимальный заказ: {minOrder} ₽.<br/>Нужно добавить еще <b>{remainingForMin} ₽</b>
            </div>
          </div>
        )}

        {/* КОНТАКТЫ */}
        <div className="space-y-3">
          <h3 className="text-tg-hint text-[10px] uppercase font-bold ml-1 tracking-widest">
            Контактные данные <span className="text-red-500 text-sm align-middle">*</span>
          </h3>
          
          <div 
            ref={phoneInputRef} 
            className={`bg-tg-secondary-bg rounded-xl p-4 flex items-center gap-3 border transition-all ${!isPhoneValid && phone.length > 5 ? 'border-red-500/30 shadow-[0_0_10px_rgba(239,68,68,0.1)]' : 'border-transparent'}`}
          >
            <Phone size={18} className={isPhoneValid ? "text-green-500" : "text-tg-hint"} />
            <InputMask 
                mask="+7 (999) 999-99-99" 
                value={phone} 
                onChange={(e) => setPhone(e.target.value)} 
                onFocus={handleInputFocus} 
                className="bg-transparent w-full outline-none text-sm font-bold text-tg-text" 
                placeholder="+7 (___) ___-__-__" 
                type="tel" 
            />
          </div>
        </div>

        {/* ПРОМОКОД */}
        <div className="space-y-1 mt-2">
            <h3 className="text-tg-hint text-[10px] uppercase font-bold ml-1 tracking-widest">Промокод</h3>
            <div className="bg-tg-secondary-bg rounded-xl p-2 flex items-center gap-2 border border-transparent">
                {!appliedPromo ? (
                    <>
                        <input 
                            type="text" 
                            value={promoCodeInput}
                            onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())}
                            placeholder="Введите промокод" 
                            className="bg-transparent w-full outline-none text-sm font-bold text-tg-text pl-2 uppercase placeholder:normal-case placeholder:font-medium"
                        />
                        <button 
                            onClick={handleApplyPromo}
                            disabled={promoLoading || !promoCodeInput.trim()}
                            className="bg-tg-button text-white text-xs font-bold py-2.5 px-4 rounded-lg disabled:opacity-50 transition-active active:scale-95 shadow-sm"
                        >
                            {promoLoading ? '...' : 'Применить'}
                        </button>
                    </>
                ) : (
                    <div className="flex items-center justify-between w-full pl-2 pr-1 py-1">
                        <div className="flex items-center gap-2">
                            <span className="bg-green-500/10 text-green-500 text-xs font-bold px-2 py-1 rounded border border-green-500/20">
                                {appliedPromo.code}
                            </span>
                            <span className="text-sm font-medium text-tg-text">
                                Скидка: <span className="font-bold text-green-500">-{Math.floor(promoDiscountAmt)} ₽</span>
                            </span>
                        </div>
                        <button onClick={handleRemovePromo} className="text-red-500 p-2 bg-red-500/10 rounded-lg active:scale-90 transition-transform">
                            ✕
                        </button>
                    </div>
                )}
            </div>
            {promoError && <p className="text-red-500 text-[11px] ml-2 mt-1 font-medium">{promoError}</p>}
        </div>

        {/* БОНУСЫ (ЗАЩИТА ОТ СУММИРОВАНИЯ) */}
        {availableBonuses > 0 && (
            <div 
                className={`bg-tg-secondary-bg rounded-xl p-4 border transition-all ${
                    useBonuses ? 'border-orange-500 bg-orange-500/10' : 'border-orange-500/20'
                } ${appliedPromo ? 'opacity-50 grayscale' : ''}`}
            >
                <div 
                    className="flex items-center justify-between" 
                    onClick={() => { 
                        // БЛОКИРОВКА КЛИКА ПРИ АКТИВНОМ ПРОМОКОДЕ
                        if (appliedPromo) {
                            triggerNotification('warning');
                            showAlert('Бонусы нельзя использовать одновременно с промокодом. Скидки не суммируются!', 'Внимание');
                            return;
                        }
                        setUseBonuses(!useBonuses); 
                        triggerHaptic('selection'); 
                    }}
                >
                    <div className="flex items-center gap-3">
                        <Gift size={20} className={useBonuses ? "text-orange-500" : "text-tg-hint"} />
                        <div>
                            <span className="font-bold text-sm block">Списать бонусы</span>
                            {appliedPromo ? (
                                <span className="text-[10px] text-red-500 font-bold">Недоступно при промокоде</span>
                            ) : (
                                <span className="text-[10px] text-tg-hint">Доступно: {availableBonuses} ₽ (макс. {maxBonusPercent}%)</span>
                            )}
                        </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        useBonuses ? 'bg-orange-500 border-orange-500' : 'border-tg-hint/30'
                    }`}>
                        {useBonuses && <Check size={14} className="text-white" />}
                    </div>
                </div>
            </div>
        )}

        {/* КОММЕНТАРИЙ */}
        <div className="bg-tg-secondary-bg rounded-xl p-4 flex items-start gap-3">
            <MessageSquare size={18} className="text-tg-hint mt-0.5" />
            <textarea 
                placeholder="Инструкции для курьера..." 
                className="bg-transparent w-full outline-none resize-none h-12 text-sm text-tg-text placeholder:font-medium" 
                value={comment} 
                onChange={e => setComment(e.target.value)} 
            />
        </div>

        {/* СПОСОБ ОПЛАТЫ */}
        <div className="pb-4">
            <h3 className="text-tg-hint text-[10px] uppercase font-bold ml-1 mb-2 tracking-widest">Способ оплаты</h3>
            
            <div className="bg-tg-secondary-bg rounded-xl p-4 border border-green-500/20 bg-green-500/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Banknote size={20} className="text-green-600" />
                    <div>
                        <span className="font-bold text-sm block">Наличными / Переводом</span>
                        <span className="text-[10px] text-tg-hint tracking-tight font-medium">Оплата при получении курьеру</span>
                    </div>
                </div>
                <Check size={18} className="text-green-600" />
            </div>
        </div>
      </div>

      {/* КНОПКА ПОДТВЕРЖДЕНИЯ (Нижний бар) */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-tg-bg border-t border-gray-100/10 z-50 pb-safe shadow-[0_-4px_25px_rgba(0,0,0,0.08)]">
        <div className="mb-3 flex justify-between text-[11px] text-tg-hint px-1 font-medium flex-wrap gap-1">
            <div className="flex gap-3">
                <span>Товары: {cartTotal} ₽</span>
                {promoDiscountAmt > 0 && <span className="text-green-500">Промо: -{Math.floor(promoDiscountAmt)} ₽</span>}
                {bonusAmt > 0 && <span className="text-orange-500">Бонусы: -{bonusAmt} ₽</span>}
            </div>
            <span className="text-green-500 font-bold uppercase tracking-tighter">Доставка: {deliveryPrice === 0 ? 'Бесплатно' : `${deliveryPrice} ₽`}</span>
        </div>
        <button 
            onClick={handleSubmit} 
            disabled={isSubmitting || !isFormValid} 
            className={`w-full py-4 rounded-xl font-bold text-lg flex justify-center items-center gap-2 transition-all active:scale-95 ${isFormValid && !isSubmitting ? 'bg-tg-button text-white shadow-lg shadow-tg-button/20' : 'bg-tg-secondary-bg text-tg-hint cursor-not-allowed opacity-60'}`}
        >
            {buttonText}
        </button>
      </div>
    </div>
  );
};

export default CheckoutPage;
